import { useState } from 'react';
import { Button, Paragraph, Stack } from '@customerio/pluma-components/react';
import { FileInput } from '@/components/file-input';
import { getUploadFunction } from '@/services/uploads';
import { useSignCollection } from '@/services/db/collection/mutations';
import { toSignResponse, type ImportSource } from './collection-payload';

const ACCEPTED_EXTENSIONS = ['csv', 'json'];
const EXTENSION_TO_MIME: Record<string, string> = { csv: 'text/csv', json: 'application/json' };
export const FILE_INPUT_LABEL = 'Drag a CSV or JSON file here or browse to upload';

type ImportSourceFieldProps = {
  value: ImportSource | null;
  onChange: (source: ImportSource | null) => void;
  workspaceId: number;
  maxFileSizeBytes?: number;
};

function fileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() ?? '';
}

export function ImportSourceField({ value, onChange, workspaceId, maxFileSizeBytes }: ImportSourceFieldProps) {
  const sign = useSignCollection({ workspaceId });
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelected = async (file: File) => {
    const extension = fileExtension(file.name);
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      setError('Choose a CSV or JSON file');
      return;
    }
    if (maxFileSizeBytes != null && file.size > maxFileSizeBytes) {
      setError(`File is too large. The maximum size is ${Math.floor(maxFileSizeBytes / 1_048_576)} MB.`);
      return;
    }
    setError(null);
    setIsUploading(true);
    try {
      const contentType = EXTENSION_TO_MIME[extension] ?? file.type;
      const signature = await sign.mutateAsync({ size: String(file.size), type: contentType });
      const { uploadFn, data } = getUploadFunction(toSignResponse(signature, contentType));
      const { key } = await uploadFn(file, data, {});
      onChange({ kind: 's3', s3Import: key, filename: file.name });
    } catch {
      setError("File couldn't be uploaded. Try again.");
    } finally {
      setIsUploading(false);
    }
  };

  if (value != null && value.kind === 's3') {
    return (
      <Stack gap="100">
        <Paragraph>{value.filename}</Paragraph>
        <Button variant="tertiary" onClick={() => onChange(null)}>
          Remove
        </Button>
      </Stack>
    );
  }

  return (
    <FileInput
      label={FILE_INPUT_LABEL}
      accept=".csv,.json"
      isDisabled={isUploading}
      error={error ?? undefined}
      onFileSelected={(file) => void handleFileSelected(file)}
    />
  );
}
